import React, { useState } from 'react'
import { makeStyles } from '@material-ui/core/styles'
import Paper from '@material-ui/core/Paper'
import Typography from '@material-ui/core/Typography'
import TextField from '@material-ui/core/TextField'
import Button from '@material-ui/core/Button'
import MenuItem from '@material-ui/core/MenuItem'
import Select from '@material-ui/core/Select'
import Switch from '@material-ui/core/Switch'
import Snackbar from '@material-ui/core/Snackbar'
import MuiAlert from '@material-ui/lab/Alert'
import SaveIcon from '@material-ui/icons/Save'
import LockIcon from '@material-ui/icons/Lock'

var useStyles = makeStyles(function(theme) {
  return {
    root: { maxWidth: 640 },
    section: { marginBottom: theme.spacing(3) },
    sectionTitle: {
      fontSize: 11,
      fontWeight: 700,
      color: '#888',
      textTransform: 'uppercase',
      letterSpacing: '0.08em',
      marginBottom: theme.spacing(1.5),
    },
    card: {
      border: '1px solid #e0e0e0',
      borderRadius: 10,
      overflow: 'hidden',
    },
    row: {
      padding: '14px 20px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      gap: 20,
      borderBottom: '1px solid #f0f0f0',
      '&:last-child': { borderBottom: 'none' },
    },
    rowLabel: { fontWeight: 600, fontSize: 14, color: '#1a2540' },
    rowDesc: { fontSize: 12, color: '#888', marginTop: 2 },
    input: { minWidth: 220 },
    saveBtn: {
      background: 'linear-gradient(135deg, #6366F1, #8B5CF6)',
      color: '#fff',
      textTransform: 'none',
      fontWeight: 600,
      padding: '8px 20px',
      borderRadius: 8,
      '&:hover': { opacity: 0.88, background: 'linear-gradient(135deg, #6366F1, #8B5CF6)' },
    },
    note: {
      fontSize: 11,
      color: '#aaa',
      marginTop: 4,
      display: 'flex',
      alignItems: 'center',
      gap: 4,
    },
  }
})

function load(key, def) {
  var v = localStorage.getItem(key)
  if (v === null) return def
  if (def === true || def === false) return v === 'true'
  return v
}

export default function VoiceSettings(props) {
  var classes = useStyles()
  var [host, setHost] = useState(function() { return load('lf_host', 'https://cloud.langfuse.com') })
  var [pub, setPub] = useState(function() { return load('lf_public_key', '') })
  var [sec, setSec] = useState(function() { return load('lf_secret_key', '') })
  var [openaiKey, setOpenaiKey] = useState(function() { return load('lf_openai_key', '') })
  var [refresh, setRefresh] = useState(function() { return load('lf_refresh_interval', 'off') })
  var [showFree, setShowFree] = useState(function() { return load('lf_show_free', true) })
  var [snack, setSnack] = useState(false)
  var [snackMsg, setSnackMsg] = useState('Settings saved successfully')
  var [snackSeverity, setSnackSeverity] = useState('success')

  function handleSave() {
    var resolvedHost = host.trim() || 'https://cloud.langfuse.com'
    localStorage.setItem('lf_host', resolvedHost)
    localStorage.setItem('lf_public_key', pub.trim())
    localStorage.setItem('lf_secret_key', sec.trim())
    localStorage.setItem('lf_openai_key', openaiKey.trim())
    localStorage.setItem('lf_refresh_interval', refresh)
    localStorage.setItem('lf_show_free', String(showFree))
    window.dispatchEvent(new Event('storage'))
    if (props.onSaved) props.onSaved()

    // Persist only non-empty credential fields to MongoDB so clearing the
    // Settings form never overwrites valid credentials stored in the database.
    var payload = {}
    if (resolvedHost !== 'https://cloud.langfuse.com') payload.langfuseHost = resolvedHost
    if (pub.trim()) payload.langfusePublicKey = pub.trim()
    if (sec.trim()) payload.langfuseSecretKey = sec.trim()
    if (openaiKey.trim()) payload.openaiApiKey = openaiKey.trim()

    if (Object.keys(payload).length === 0) {
      setSnackMsg('Display settings saved')
      setSnackSeverity('success')
      setSnack(true)
      return
    }
    fetch('/app/appConfigurations/voice-credentials', {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    })
      .then(function(res) {
        if (res.ok) {
          setSnackMsg('Settings saved and synced to app config')
          setSnackSeverity('success')
        } else {
          setSnackMsg('Settings saved locally — app config sync failed')
          setSnackSeverity('warning')
        }
        setSnack(true)
      })
      .catch(function() {
        setSnackMsg('Settings saved locally — app config sync failed')
        setSnackSeverity('warning')
        setSnack(true)
      })
  }

  return (
    <div className={classes.root}>
      {/* Langfuse Credentials */}
      <div className={classes.section}>
        <Typography className={classes.sectionTitle}>Langfuse Credentials</Typography>
        <Paper className={classes.card} elevation={0}>
          <div className={classes.row}>
            <div>
              <div className={classes.rowLabel}>Host URL</div>
              <div className={classes.rowDesc}>Your Langfuse instance URL</div>
            </div>
            <TextField
              className={classes.input}
              size="small"
              variant="outlined"
              value={host}
              onChange={function(e) { setHost(e.target.value) }}
              placeholder="https://cloud.langfuse.com"
            />
          </div>
          <div className={classes.row}>
            <div>
              <div className={classes.rowLabel}>Public Key</div>
              <div className={classes.rowDesc}>Found in Langfuse project settings</div>
            </div>
            <TextField
              className={classes.input}
              size="small"
              variant="outlined"
              value={pub}
              onChange={function(e) { setPub(e.target.value) }}
              placeholder="pk-lf-..."
            />
          </div>
          <div className={classes.row}>
            <div>
              <div className={classes.rowLabel}>Secret Key</div>
              <div className={classes.rowDesc}>Stored only in your browser</div>
            </div>
            <TextField
              className={classes.input}
              size="small"
              variant="outlined"
              type="password"
              value={sec}
              onChange={function(e) { setSec(e.target.value) }}
              placeholder="sk-lf-..."
            />
          </div>
        </Paper>
        <div className={classes.note}>
          <LockIcon style={{ fontSize: 12 }} />
          Credentials are auto-loaded from app config on startup. Values entered here override them for this browser session only.
        </div>
      </div>

      {/* AI Assistant */}
      <div className={classes.section}>
        <Typography className={classes.sectionTitle}>AI Assistant</Typography>
        <Paper className={classes.card} elevation={0}>
          <div className={classes.row}>
            <div>
              <div className={classes.rowLabel}>OpenAI API Key</div>
              <div className={classes.rowDesc}>Used for the chat assistant (gpt-4o-mini)</div>
            </div>
            <TextField
              className={classes.input}
              size="small"
              variant="outlined"
              type="password"
              value={openaiKey}
              onChange={function(e) { setOpenaiKey(e.target.value) }}
              placeholder="sk-..."
            />
          </div>
        </Paper>
      </div>

      {/* Display Settings */}
      <div className={classes.section}>
        <Typography className={classes.sectionTitle}>Display</Typography>
        <Paper className={classes.card} elevation={0}>
          <div className={classes.row}>
            <div>
              <div className={classes.rowLabel}>Auto Refresh</div>
              <div className={classes.rowDesc}>Automatically reload data</div>
            </div>
            <Select
              variant="outlined"
              size="small"
              value={refresh}
              onChange={function(e) { setRefresh(e.target.value) }}
              style={{ minWidth: 140, height: 38 }}
            >
              <MenuItem value="off">Off</MenuItem>
              <MenuItem value="30">Every 30s</MenuItem>
              <MenuItem value="60">Every 1 min</MenuItem>
              <MenuItem value="300">Every 5 min</MenuItem>
            </Select>
          </div>
          <div className={classes.row}>
            <div>
              <div className={classes.rowLabel}>Show Free Services</div>
              <div className={classes.rowDesc}>Include $0 cost services in charts</div>
            </div>
            <Switch
              checked={showFree}
              onChange={function(e) { setShowFree(e.target.checked) }}
              color="primary"
            />
          </div>
        </Paper>
      </div>

      {/* About */}
      <div className={classes.section}>
        <Typography className={classes.sectionTitle}>About</Typography>
        <Paper className={classes.card} elevation={0}>
          <div style={{ padding: '14px 20px' }}>
            <Typography style={{ fontSize: 14, fontWeight: 600, color: '#1a2540' }}>
              Voice Agent Analytics v1.0
            </Typography>
            <Typography style={{ fontSize: 12, color: '#888', marginTop: 4 }}>
              Langfuse-powered dashboard for monitoring voice agent sessions, costs, and usage patterns.
            </Typography>
          </div>
        </Paper>
      </div>

      <Button
        className={classes.saveBtn}
        variant="contained"
        disableElevation
        startIcon={<SaveIcon />}
        onClick={handleSave}
      >
        Save Settings
      </Button>

      <Snackbar open={snack} autoHideDuration={4000} onClose={function() { setSnack(false) }}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}>
        <MuiAlert elevation={6} variant="filled" severity={snackSeverity} onClose={function() { setSnack(false) }}>
          {snackMsg}
        </MuiAlert>
      </Snackbar>
    </div>
  )
}
